'use client';

import { HStack, Text, VStack } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { ForwardRefRenderFunction, forwardRef } from 'react';
import { isZmyleTransactionIntent } from 'utils/helpers';
import { GiftcardTransactionIntentItem } from './GiftcardTransactionIntentItem';

interface Props {
  transaction: AdaptedGiftcardTransaction | AdaptedGiftcardTransactionIntent;
  content: GiftcardTransactionsListContent;
}

const GiftcardTransactionItemBase: ForwardRefRenderFunction<
  HTMLDivElement,
  Props
> = (props, ref) => {
  const { transaction, content } = props;

  if (isZmyleTransactionIntent(transaction)) {
    return (
      <GiftcardTransactionIntentItem
        ref={ref}
        transaction={transaction}
        content={content}
      />
    );
  }

  const {
    benefitTransactionRef,
    confirmed,
    statementDescriptor,
    type,
    formatted,
    amount,
  } = transaction;

  const transactionTypeLabel: Record<GiftcardTransactionType, string> = {
    DEBIT: content.debit,
    CREDIT: content.credit,
    CANCELLATION: content.cancellation,
    DEPRECIATION: content.depreciation,
    REFUND: content.refund,
  };

  return (
    <HStack
      justify="space-between"
      data-testid="giftcard-transaction-item"
      key={transaction.id}
      w="full"
      as={motion.div}
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1, transition: { delay: 0.25 } }}
      transition={{ type: 'spring' }}
      ref={ref}
    >
      <VStack
        align="flex-start"
        spacing="0.25rem"
        data-testid="giftcard-transaction-item-infos"
      >
        <Text fontSize="0.9375rem" color="network.accent">
          {formatted.confirmedAt}
        </Text>

        <Text fontSize="1.125rem">{statementDescriptor}</Text>

        <Text fontSize="0.9375rem" color="dynamic.accent">
          {transactionTypeLabel[type]}

          {benefitTransactionRef?.benefitTypeName &&
            ` - ${benefitTransactionRef?.benefitTypeName}`}
        </Text>
      </VStack>

      <Text fontSize="1.25rem" color={amount < 0 ? 'red.500' : 'blue.800'}>
        {`${confirmed && amount > 0 ? '+' : ''}${formatted.amount}`}
      </Text>
    </HStack>
  );
};

export const GiftcardTransactionItem = forwardRef(GiftcardTransactionItemBase);
